import React, { useState, useRef, useEffect } from 'react';

/**
 * SearchableSelect — a drop-in replacement for <select> with a live search filter.
 *
 * Props:
 *   value        — current selected value (string)
 *   onChange     — (value: string) => void
 *   options      — Array of { value, label } objects
 *   placeholder  — text shown when nothing is selected
 *   style        — style applied to the trigger button (mirrors inputStyle)
 *   disabled     — boolean
 */
export default function SearchableSelect({ value, onChange, options = [], placeholder = '-- Select --', style = {}, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search input when opening
  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const selectedLabel = options.find(o => o.value === value)?.label ?? '';

  const triggerStyle = {
    ...style,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    userSelect: 'none',
    opacity: disabled ? 0.6 : 1,
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 9999,
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
    maxHeight: '220px',
    display: 'flex',
    flexDirection: 'column',
  };

  const searchInputStyle = {
    width: '100%',
    padding: '7px 8px',
    border: 'none',
    borderBottom: '1px solid var(--border-color)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    fontSize: '12px',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const listStyle = {
    overflowY: 'auto',
    flex: 1,
  };

  const optionStyle = (isSelected) => ({
    padding: '7px 10px',
    cursor: 'pointer',
    fontSize: '13px',
    background: isSelected ? 'var(--accent-color)' : 'transparent',
    color: isSelected ? '#fff' : 'var(--text-primary)',
  });

  return (
    <div ref={containerRef} style={{ position: 'relative', marginBottom: style.marginBottom ?? 0 }}>
      <div
        style={triggerStyle}
        onClick={() => { if (!disabled) { setOpen(o => !o); setQuery(''); } }}
        role="combobox"
        aria-expanded={open}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(o => !o); }
          if (e.key === 'Escape') { setOpen(false); setQuery(''); }
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selectedLabel ? 'var(--text-primary)' : 'var(--text-tertiary)', fontSize: '13px' }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--text-tertiary)', flexShrink: 0 }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {open && (
        <div style={dropdownStyle}>
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            style={searchInputStyle}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setOpen(false); setQuery(''); }
              if (e.key === 'Enter' && filtered.length === 1) {
                onChange(filtered[0].value);
                setOpen(false);
                setQuery('');
              }
            }}
          />
          <div style={listStyle}>
            {filtered.length === 0 ? (
              <div style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                No results
              </div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  style={optionStyle(opt.value === value)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(opt.value);
                    setOpen(false);
                    setQuery('');
                  }}
                  onMouseEnter={(e) => {
                    if (opt.value !== value) e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.07))';
                  }}
                  onMouseLeave={(e) => {
                    if (opt.value !== value) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
